// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useLocation, useNavigate } from '@tanstack/react-router';

// material ui icons
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import PivotTableChartRoundedIcon from '@mui/icons-material/PivotTableChartRounded';
import StarHalfRoundedIcon from '@mui/icons-material/StarHalfRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';

// material ui
import { Badge, Tooltip } from '@mui/material';
import { ToolbarButton } from '@mui/x-data-grid-premium';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

// ─── Fullscreen ──────────────────────────────────────────────────────────────

export const FullscreenButton = () => {
  const { t } = useTranslation('portfolio-analysis');
  const [searchParams, setSearchParams] = useSearchParams();
  const isFullScreen = searchParams.get('fullscreen') === 'true';

  const toggle = () => {
    setSearchParams((prev: URLSearchParams) => {
      const next = new URLSearchParams(prev);
      if (isFullScreen) {
        next.delete('fullscreen');
      } else {
        next.set('fullscreen', 'true');
      }
      return next;
    });
  };

  return (
    <Tooltip
      title={
        isFullScreen
          ? t('toolbar.fullscreen_exit')
          : t('toolbar.fullscreen_enter')
      }
    >
      <ToolbarButton onClick={toggle}>
        {isFullScreen ? (
          <FullscreenExitRoundedIcon fontSize="small" />
        ) : (
          <FullscreenRoundedIcon fontSize="small" />
        )}
      </ToolbarButton>
    </Tooltip>
  );
};

// ─── Expand / Collapse ───────────────────────────────────────────────────────

interface ExpandCollapseButtonProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const ExpandCollapseButton = ({
  isCollapsed,
  onToggle,
}: ExpandCollapseButtonProps) => {
  const { t } = useTranslation('portfolio-analysis');

  return (
    <Tooltip
      title={isCollapsed ? t('toolbar.expand_all') : t('toolbar.collapse_all')}
    >
      <ToolbarButton onClick={onToggle}>
        {isCollapsed ? (
          <UnfoldMoreRoundedIcon fontSize="small" />
        ) : (
          <UnfoldLessRoundedIcon fontSize="small" />
        )}
      </ToolbarButton>
    </Tooltip>
  );
};

// ─── Favorites ───────────────────────────────────────────────────────────────

interface FavoritesButtonProps {
  isFavoritesFiltered: boolean;
  onToggle: (newValue: boolean) => void;
}

export const FavoritesButton = ({
  isFavoritesFiltered,
  onToggle,
}: FavoritesButtonProps) => {
  const { t } = useTranslation('portfolio-analysis');

  return (
    <Tooltip title={t('toolbar.only_favorites')}>
      <ToolbarButton onClick={() => onToggle(!isFavoritesFiltered)}>
        {isFavoritesFiltered ? (
          <StarRoundedIcon fontSize="small" color="warning" />
        ) : (
          <StarHalfRoundedIcon fontSize="small" />
        )}
      </ToolbarButton>
    </Tooltip>
  );
};

// ─── Pivot view ──────────────────────────────────────────────────────────────

export const PivotViewButton = () => {
  const { t } = useTranslation('portfolio-analysis');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPivotView = location.pathname.includes('/pivot/');

  const toggle = () => {
    navigate({
      to: '/$accountId/energyxplorer/portfolio/$analysisType/pivot',
      search: Object.fromEntries(searchParams),
    });
  };

  return (
    <Tooltip
      title={
        isPivotView
          ? t('toolbar.switch_to_grid_view')
          : t('toolbar.switch_to_pivot_view')
      }
    >
      <ToolbarButton onClick={toggle}>
        <Badge
          badgeContent="Beta"
          color="success"
          sx={{
            '& .MuiBadge-badge': {
              right: -4,
              top: -4,
              fontSize: '0.6rem',
              height: 16,
              minWidth: 16,
              padding: '0 4px',
            },
          }}
        >
          {isPivotView ? (
            <TableRowsRoundedIcon fontSize="small" />
          ) : (
            <PivotTableChartRoundedIcon fontSize="small" />
          )}
        </Badge>
      </ToolbarButton>
    </Tooltip>
  );
};
